// src/screens/VoyantBreakPrompt2.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Close Screen 1 — Voyant Break 2. Adviser-only.
// Dynamic update list from plan decisions. Read back S1 scenario values.
// Mark complete → set voyantS1Captured, navigate to What Today Changed.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useRef } from 'react';
import { ClientData } from '../types/ClientData';
import { formatCurrency } from '../utils/format';
import Scoreboard from '../components/Scoreboard';
import { useCommaInput } from '../utils/useCommaInput';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

type VoyantUpdateItem = {
  step: number;
  label: string;
  detail: string;
};

function generateVoyantUpdateList(data: ClientData): VoyantUpdateItem[] {
  const items: VoyantUpdateItem[] = [];
  let step = 1;

  // Employer pension (existing + dividend reduction)
  const totalPension = (data.pensionCompanyFunded || 0) +
                       (data.planPensionFromDividendReduction || 0);
  if (totalPension > 0) {
    items.push({
      step: step++,
      label: 'Employer pension contribution',
      detail: `Set to ${formatCurrency(totalPension)}/month`
        + (data.planDividendReductionCommitted
          ? ` (includes ${formatCurrency(Math.round(data.planPensionFromDividendReduction))}/mo from reduced dividends)`
          : '')
    });
  }

  // ISA contribution
  if ((data.planIsaMonthlyContribution || 0) > 0) {
    items.push({
      step: step++,
      label: 'ISA monthly contribution',
      detail: `Set to ${formatCurrency(data.planIsaMonthlyContribution)}/month`
    });
  }

  // Mortgage overpayment → brings payoff date forward
  if ((data.planMortgageOverpayment || 0) > 0) {
    items.push({
      step: step++,
      label: 'Mortgage payoff date',
      detail: `Overpaying ${formatCurrency(data.planMortgageOverpayment)}/month`
        + ((data.planMortgageAnnualLumpSum || 0) > 0
          ? ` + ${formatCurrency(data.planMortgageAnnualLumpSum)}/year lump sum`
          : '')
        + (data.planMortgagePayoffAge > 0
          ? ` — new payoff age ${data.planMortgagePayoffAge}`
          : '')
    });
  }

  // EF redirect (if EF was not complete and a top-up was set)
  if ((data.planEfMonthlyTopUp || 0) > 0 && data.planEfRedirectTo) {
    items.push({
      step: step++,
      label: 'Emergency fund top-up redirect',
      detail: `${formatCurrency(data.planEfMonthlyTopUp)}/month until ${data.planEfCompleteDate || 'funded'},`
        + ` then redirects to ${data.planEfRedirectTo.toUpperCase()}`
    });
  }

  // Dividend reduction (adviser should reduce dividend income in Voyant to match)
  if ((data.planDividendReduction || 0) > 0) {
    const annualReduction = data.planDividendReduction * 12;
    const currentAnnualDividends = (data.dividends || 0);
    items.push({
      step: step++,
      label: 'Dividend income (reduce in Voyant)',
      detail: `Reduce annual dividends by ${formatCurrency(annualReduction)}/year`
        + ` to ${formatCurrency(Math.max(0, currentAnnualDividends - annualReduction))}/year`
    });
  }

  return items;
}

const fmt = formatCurrency;

export default function VoyantBreakPrompt2({ data, onUpdate }: Props) {
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Read-back fields — initialise from stored values
  const [retirementAge, setRetirementAge] = useState<string>(
    data.voyantS1RetirementAge > 0 ? data.voyantS1RetirementAge.toString() : ''
  );
  const [potAtTargetAge, setPotAtTargetAge] = useState(data.voyantS1PotAtTargetAge || 0);
  const [lumpSumNeeded, setLumpSumNeeded] = useState(data.voyantS1LumpSumNeeded || 0);
  const [annualSavingsNeeded, setAnnualSavingsNeeded] = useState(data.voyantS1AnnualSavingsNeeded || 0);
  // Brief 12 additional read-back fields
  const [annualIncome, setAnnualIncome] = useState(data.voyantS1AnnualIncome || 0);
  const [gapRemaining, setGapRemaining] = useState(data.voyantS1GapRemaining || 0);

  // Create comma input hooks
  const potInput = useCommaInput(potAtTargetAge, setPotAtTargetAge);
  const lumpSumInput = useCommaInput(lumpSumNeeded, setLumpSumNeeded);
  const annualSavingsInput = useCommaInput(annualSavingsNeeded, setAnnualSavingsNeeded);
  const annualIncomeInput = useCommaInput(annualIncome, setAnnualIncome);
  const gapRemainingInput = useCommaInput(gapRemaining, setGapRemaining);

  const isComplete = data.voyantS1Captured;

  useEffect(() => {
    if (timerRunning) {
      intervalRef.current = setInterval(() => setElapsedSeconds(prev => prev + 1), 1000);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [timerRunning]);

  const targetSeconds = 8 * 60;
  const remaining = Math.max(0, targetSeconds - elapsedSeconds);
  const formatTime = (s: number) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  // Dynamic update list from plan decisions
  const updateItems = generateVoyantUpdateList(data);

  const handleMarkComplete = () => {
    const age = retirementAge ? parseInt(retirementAge) : 0;

    onUpdate({
      voyantS1Captured: true,
      voyantS1RetirementAge: age,
      voyantS1PotAtTargetAge: potAtTargetAge || 0,
      voyantS1LumpSumNeeded: lumpSumNeeded || 0,
      voyantS1AnnualSavingsNeeded: annualSavingsNeeded || 0,
      voyantS1AnnualIncome: annualIncome || 0,
      voyantS1GapRemaining: gapRemaining || 0,
      voyantBreak2Complete: true,
    });
    setTimerRunning(false);
  };

  return (
    <div className="flex flex-col h-full">
      <Scoreboard data={data} />

      <div className="flex-1 overflow-y-auto p-6 max-w-4xl mx-auto w-full">
        <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-4 mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl">⏸️</span>
            <div>
              <h2 className="text-lg font-bold text-amber-800">Voyant Session Prep</h2>
              <p className="text-sm text-amber-600">Set this up in Voyant before the session begins. These numbers open the conversation, not interrupt it.</p>
            </div>
          </div>
          <div className="bg-amber-100 text-amber-700 text-xs font-medium px-3 py-1.5 rounded-full">Complete before client arrives</div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Left: What to update in Voyant */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <h3 className="text-sm font-bold text-ma-navy mb-4">What to update in Voyant</h3>

            {updateItems.length > 0 ? (
              <div className="space-y-4">
                {updateItems.map((item) => (
                  <div key={item.step} className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                      {item.step}
                    </span>
                    <div>
                      <div className="text-sm font-semibold text-gray-800">{item.label}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{item.detail}</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-gray-50 rounded-xl p-4 text-center">
                <p className="text-sm text-gray-500 italic">No changes to update — the do-nothing scenario is already in Voyant.</p>
              </div>
            )}
          </div>

          {/* Right: Read back and enter */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <h3 className="text-sm font-bold text-ma-navy mb-4">Note these numbers before the session</h3>
            <div className="space-y-4">
              {/* 1. New projected retirement age */}
              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wide font-medium">New projected retirement age</label>
                <input
                  type="number"
                  value={retirementAge}
                  onChange={e => setRetirementAge(e.target.value)}
                  disabled={isComplete}
                  className="w-full mt-1.5 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-lg font-bold text-ma-navy outline-none focus:ring-2 focus:ring-ma-navy/20"
                  placeholder="e.g. 58"
                />
              </div>

              {/* 2. New pot at freedom age */}
              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wide font-medium">New pot at freedom age (target age)</label>
                <div className="flex items-center mt-1.5 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5">
                  <span className="text-gray-400 text-sm mr-1">£</span>
                  <input
                    {...potInput}
                    disabled={isComplete}
                    className="flex-1 bg-transparent text-lg font-bold text-ma-navy outline-none"
                    placeholder="0"
                  />
                </div>
              </div>

              {/* 3. Lump sum needed */}
              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wide font-medium">Lump sum needed to prevent shortfall</label>
                <div className="flex items-center mt-1.5 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5">
                  <span className="text-gray-400 text-sm mr-1">£</span>
                  <input
                    {...lumpSumInput}
                    disabled={isComplete}
                    className="flex-1 bg-transparent text-lg font-bold text-ma-navy outline-none"
                    placeholder="0"
                  />
                </div>
              </div>

              {/* 4. Annual income at freedom */}
              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wide font-medium">Annual income at freedom age</label>
                <div className="flex items-center mt-1.5 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5">
                  <span className="text-gray-400 text-sm mr-1">£</span>
                  <input
                    {...annualIncomeInput}
                    disabled={isComplete}
                    className="flex-1 bg-transparent text-lg font-bold text-ma-navy outline-none"
                    placeholder="0"
                  />
                  <span className="text-gray-400 text-sm ml-1">/year</span>
                </div>
              </div>

              {/* 5. Annual additional savings needed */}
              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wide font-medium">Annual additional savings needed</label>
                <div className="flex items-center mt-1.5 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5">
                  <span className="text-gray-400 text-sm mr-1">£</span>
                  <input
                    {...annualSavingsInput}
                    disabled={isComplete}
                    className="flex-1 bg-transparent text-lg font-bold text-ma-navy outline-none"
                    placeholder="0"
                  />
                  <span className="text-gray-400 text-sm ml-1">/year</span>
                </div>
              </div>

              {/* 6. Gap remaining */}
              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wide font-medium">Gap remaining</label>
                <div className="flex items-center mt-1.5 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5">
                  <span className="text-gray-400 text-sm mr-1">£</span>
                  <input
                    {...gapRemainingInput}
                    disabled={isComplete}
                    className="flex-1 bg-transparent text-lg font-bold text-ma-navy outline-none"
                    placeholder="0"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Timer — below the update list, de-emphasised */}
        <div className="bg-white rounded-2xl border border-gray-200 p-4 mb-6 text-center">
          <div className="text-2xl font-mono font-bold text-ma-navy mb-2">
            {timerRunning ? formatTime(remaining) : '8:00'}
          </div>
          {!timerRunning && !isComplete && (
            <button onClick={() => setTimerRunning(true)} className="border-2 border-ma-navy text-ma-navy px-6 py-2 rounded-xl text-sm font-medium hover:bg-ma-navy/5">
              Start the 8-minute Voyant update →
            </button>
          )}
          {timerRunning && (
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden mt-3 max-w-md mx-auto">
              <div className="h-full bg-ma-gold rounded-full" style={{ width: `${Math.min(100, (elapsedSeconds / targetSeconds) * 100)}%` }} />
            </div>
          )}
        </div>

        {!isComplete ? (
          <div className="text-center">
            <button onClick={handleMarkComplete} className="bg-green-600 text-white px-8 py-3 rounded-xl text-sm font-semibold hover:bg-green-700">
              Mark Voyant Session Prep Complete
            </button>
            <p className="text-xs text-gray-400 mt-2">This saves the S1 scenario and unlocks "What Today Changed"</p>
          </div>
        ) : (
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 text-sm font-medium px-4 py-2 rounded-full">
              <span>✓</span> Voyant Session Prep complete — S1 scenario captured
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
